// global styling
import '../style.css'

// i18n
import '../locales/i18n'

// scroll bar
import 'simplebar/src/simplebar.css'

// editor
import 'react-quill/dist/quill.snow.css'

// slick-carousel
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css'
import 'react-lazy-load-image-component/src/effects/opacity.css'
import 'react-lazy-load-image-component/src/effects/black-and-white.css'

import { ReactElement, ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
// next
import { NextPage } from 'next'
import Head from 'next/head'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'
//
import { Provider as ReduxProvider } from 'react-redux'
// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers'
// redux
import { store } from '../redux/store'
// utils
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
// contexts
import { AuthProvider } from '~/contexts/AuthContext'
import { SettingsProvider } from '~/contexts/SettingsContext'
import { CollapseDrawerProvider } from '~/contexts/CollapseDrawerContext'
import { ProductPopupProvider } from '~/contexts/ProductPopupContext'
import {
  CaptchaChallengeProvider,
  CaptchaScriptStatus,
} from '~/contexts/CaptchaChallengeContext'
// theme
import ThemeProvider from '../theme'
// components
import { SettingsValueProps } from 'components/settings/type'
import ProgressBar from 'components/ProgressBar'
import NotistackProvider from 'components/NotistackProvider'
import MotionLazyContainer from 'components/animate/MotionLazyContainer'

import { pageview } from 'helpers/google-tag-manager'
import { GTM_ID } from 'constants/index'
import { initAnalytics } from 'helpers/analytics'
import { RoutingProvider } from '~/contexts/RoutingContext'
import {
  FlagProvider,
  UnleashClient,
} from '@unleash/proxy-client-react'
import flagConfig from 'configs/feature-flag'
initAnalytics()

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

type NextPageWithLayout = NextPage & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement) => ReactNode
}

interface MyAppProps extends AppProps {
  settings: SettingsValueProps
  Component: NextPageWithLayout
}

export default function MyApp(props: MyAppProps) {
  const router = useRouter()
  // Created once and reused for the life of the tab — a plain `const` here
  // was re-instantiated on every MyApp render (e.g. every route change),
  // silently wiping the React Query cache each time and undermining callers
  // that rely on `staleTime: Infinity` to mean "fetched once per session".
  const queryClientRef = useRef<QueryClient>()
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient()
  }
  const queryClient = queryClientRef.current
  const { Component, pageProps } = props

  const settings: SettingsValueProps = {
    themeMode: 'light',
    themeDirection: 'ltr',
    themeContrast: 'default',
    themeLayout: 'horizontal',
    themeColorPresets: 'default',
    themeStretch: false,
  }

  const getLayout = Component.getLayout ?? ((page) => page)
  //clients unleash config — same re-instantiation problem as queryClient
  // above; a fresh client on every render meant `useFlag` reads inside
  // AuthContext could see a client that never finished loading toggles.
  const unleashClientRef = useRef<UnleashClient>()
  if (!unleashClientRef.current) {
    unleashClientRef.current = new UnleashClient(flagConfig)
  }
  const client = unleashClientRef.current

  // Turnstile is fetched when the first challenge asks for it, not on every
  // page view. A retry bumps the attempt so next/script mounts a fresh tag.
  const [loadTurnstile, setLoadTurnstile] = useState(false)
  const [turnstileScriptStatus, setTurnstileScriptStatus] =
    useState<CaptchaScriptStatus>('loading')
  const [turnstileScriptAttempt, setTurnstileScriptAttempt] =
    useState(0)

  const requestTurnstileScript = useCallback(() => {
    setLoadTurnstile(true)
  }, [])

  const retryTurnstileScript = useCallback(() => {
    setLoadTurnstile(true)
    setTurnstileScriptStatus('loading')
    setTurnstileScriptAttempt((attempt) => attempt + 1)
  }, [])

  // The fragment never reaches the network; it only makes a retry a new src.
  const turnstileScriptSrc =
    turnstileScriptAttempt === 0
      ? TURNSTILE_SCRIPT_SRC
      : `${TURNSTILE_SCRIPT_SRC}#retry=${turnstileScriptAttempt}`

  useEffect(() => {
    router.events.on('routeChangeComplete', pageview)
    return () => {
      router.events.off('routeChangeComplete', pageview)
    }
  }, [router.events])

  // Start polling once, centrally, instead of leaving it to whichever
  // useFeatureFlags() consumer happens to mount first (see helpers/feature-flag)
  // — AuthContext needs toggle data before any screen-level flag consumer
  // would otherwise have kicked this off.
  useEffect(() => {
    client.start()
    return () => client.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1, width=device-width"
        />
        <title>TipTip Content Hub</title>
      </Head>
      <Script
        id="Freshchat-js-sdk"
        src="https://wchat.freshchat.com/js/widget.js"
        async
      />
      <Script
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${GTM_ID}');
              `,
        }}
      />
      {loadTurnstile && (
        <Script
          key={turnstileScriptAttempt}
          id={`cloudflare-turnstile-${turnstileScriptAttempt}`}
          src={turnstileScriptSrc}
          strategy="afterInteractive"
          onLoad={() => setTurnstileScriptStatus('loaded')}
          onReady={() => setTurnstileScriptStatus('loaded')}
          onError={() => setTurnstileScriptStatus('failed')}
        />
      )}

      <QueryClientProvider client={queryClient}>
        {/* Wraps AuthProvider (not the other way around, as before) so
            AuthContext can read the multiple-roles flag via useFlag()/
            useFlagsStatus() directly — it gates GET /me on it. */}
        <FlagProvider unleashClient={client} startClient={false}>
          <AuthProvider>
            <ReduxProvider store={store}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <RoutingProvider router={router}>
                  <CollapseDrawerProvider>
                    <SettingsProvider defaultSettings={settings}>
                      <MotionLazyContainer>
                        <ThemeProvider>
                          <NotistackProvider>
                            <ProgressBar />
                            <ProductPopupProvider>
                              <CaptchaChallengeProvider
                                scriptStatus={turnstileScriptStatus}
                                onRequestScript={
                                  requestTurnstileScript
                                }
                                onRetryScript={retryTurnstileScript}
                              >
                                {getLayout(
                                  <Component {...pageProps} />,
                                )}
                              </CaptchaChallengeProvider>
                            </ProductPopupProvider>
                          </NotistackProvider>
                        </ThemeProvider>
                      </MotionLazyContainer>
                    </SettingsProvider>
                  </CollapseDrawerProvider>
                </RoutingProvider>
              </LocalizationProvider>
            </ReduxProvider>
          </AuthProvider>
        </FlagProvider>
      </QueryClientProvider>
    </div>
  )
}

// ----------------------------------------------------------------------

// MyApp.getInitialProps = async (context: AppContext) => {
//   const appProps = await App.getInitialProps(context)

//   const cookies = cookie.parse(
//     context.ctx.req
//       ? context?.ctx?.req?.headers?.cookie || ''
//       : document?.cookie,
//   )

//   const settings = getSettings(cookies)

//   return {
//     ...appProps,
//     settings,
//   }
// }
